import { createSlice } from "@reduxjs/toolkit";
import {
  fetchServiceExpenseNumber,
  createServiceExpense,
  fetchServiceExpenseAccs,
  fetchServiceExpenseRecord,
} from "./serviceExpenseThunk";
import { currentDateTime } from "../../../../lib/functions";
import Big from "big.js";

// Discount for a single item row: reads whichever mode is active
// (percent, the default/undefined-safe mode, or a flat amount), clamped
// so a flat amount can never exceed the item's own line total.
const getItemDiscountAmount = (item, line) => {
  if (item?.discountMode === "amount") {
    const raw = new Big(item?.discountAmt || 0);
    return raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
  }
  return line.times(item?.discount || 0).div(100);
};

const initialState = {
  supplierInfo: {},
  itemCart: [],
  payServiceCart: [],
  serviceExpenseAccs: [],

  footerValues: {
    subtotal: "0.00",
    totalTax: "0.00",
    grandTotal: "0.00",
    total_discount: "0.00",
    description: "",
    paidAmount: 0,
    dueAmount: 0,
    serviceCharge: 0,
    discountPct: 0,
    discountAmt: 0,
    taxPct: 0,
  },

  tag_field_id: [],
  selectedDiscountAcc: null,
  selectedServiceExpenseAcc: null,
  selectedTaxAcc: null,
  conversionRate: 0,
  conversionCurrency: null,

  voucherNumber: null,
  exp_id: null,
  entryDate: currentDateTime(),
  dueDate: null,
  paymentTerm: "Immediate",

  selectedDetails: null,
  loading: false,
  success: null,
  taxMode: "on_item", // or "on_total"
};

const serviceExpenseSlice = createSlice({
  name: "serviceExpense",
  initialState,
  reducers: {
    resetServiceExpense() {
      return {
        supplierInfo: {},
        itemCart: [],
        payServiceCart: [],
        serviceExpenseAccs: [],

        footerValues: {
          subtotal: "0.00",
          totalTax: "0.00",
          grandTotal: "0.00",
          total_discount: "0.00",
          description: "",
          paidAmount: 0,
          dueAmount: 0,
          serviceCharge: 0,
          discountPct: 0,
          discountAmt: 0,
          taxPct: 0,
        },

        tag_field_id: [],

        selectedDiscountAcc: null,
        selectedServiceExpenseAcc: null,   // ✅ added
        selectedTaxAcc: null,

        conversionRate: 0,
        conversionCurrency: null,

        voucherNumber: null,              

        service_ex_id: null,               
        exp_id: null,                     

        entryDate: currentDateTime(),
        dueDate: null,
        paymentTerm: "Immediate",

        selectedDetails: null,
        loading: false,
        success: null,
        taxMode: "on_item",
      };
    },

    setSupplier(state, action) {
      state.supplierInfo = action.payload || {};
    },

    addOrderRow(state, action) {
      state.itemCart.push(action.payload);
    },
    deleteOrderRow(state, action) {
      state.itemCart = state.itemCart.filter((r) => r.key !== action.payload);
    },
    updateOrderRow(state, action) {
      const { rowKey, updates } = action.payload;
      state.itemCart = state.itemCart.map((row) =>
        row.key === rowKey ? { ...row, ...updates } : row
      );
    },

    setPayServiceCart(state, action) {
      state.payServiceCart = action.payload || [];
    },

    setTagFieldId(state, action) {
      state.tagFieldId = action.payload || null;
    },

    setServiceExpenseAcc(state, action) {
      state.selectedServiceExpenseAcc = action.payload;
    },
    setDiscountAcc(state, action) {
      state.selectedDiscountAcc = action.payload;
    },
    setTaxAcc(state, action) {
      state.selectedTaxAcc = action.payload;
    },

    setConversion(state, action) {
      state.conversionCurrency = action.payload || null;
    },
    setConversionRate(state, action) {
      state.conversionRate = action.payload;
    },
    setTagFeildNameid(state, action) {
      state.tag_field_id = action.payload;
    },
    setEntryDate(state, action) {
      state.entryDate = action.payload;
    },
    setDueDate(state, action) {
      state.dueDate = action.payload;
    },

    setTaxMode(state, action) {
      state.taxMode = action.payload;
    },

    setFormField(state, action) {
      const { field, value } = action.payload;
      state[field] = value;
    },

    setFooterValues(state, action) {
      state.footerValues = { ...state.footerValues, ...action.payload };
    },

    calculateTotals(state, action) {
      const authInfo = action?.payload?.authInfo || {};
      const isIndia = authInfo?.country?.toLowerCase() === "india";
      const gstEnabled = authInfo?.gst_enabled === "yes";
      const showTax = !isIndia || gstEnabled;
      let subtotal = Big(0);
      let totalDiscount = Big(0);
      let totalTax = Big(0);

      state.itemCart.forEach((it) => {
        const qty = Big(it.quantity || 0);
        const rate = Big(it.rate || 0);
        if (it.bonus) return;

        let line = qty.times(rate);

        if (state.taxMode === "on_item") {
          const discountAmt = getItemDiscountAmount(it, line);
          const afterDiscount = line.minus(discountAmt);
          line = line.minus(discountAmt);

          const taxPer = showTax ? Big(it.tax_per || 0) : Big(0);
          const taxAmt = afterDiscount.times(taxPer).div(100);
          line = line.plus(taxAmt);

          subtotal = subtotal.plus(line);
          totalDiscount = totalDiscount.plus(discountAmt);
          totalTax = totalTax.plus(taxAmt);
        } else {
          subtotal = subtotal.plus(line);
        }
      });

      let resolvedDiscountPct = state.footerValues.discountPct;

      if (state.taxMode === "on_total") {
        const footerDiscountMode = state.footerValues.discountMode === "amount" ? "amount" : "percent";
        if (footerDiscountMode === "amount") {
          const raw = Big(state.footerValues.discountAmt || 0);
          totalDiscount = raw.gt(subtotal) ? subtotal : raw.lt(0) ? Big(0) : raw;
          // Back-computed so the printed voucher (which reads this field
          // directly) is correct even when the footer is in amount mode.
          resolvedDiscountPct = subtotal.eq(0) ? 0 : Number(totalDiscount.div(subtotal).times(100).toFixed(13));
        } else {
          const discountPct = Number(state.footerValues.discountPct) || 0;
          totalDiscount = subtotal.times(discountPct).div(100);
          resolvedDiscountPct = discountPct;
        }

        const afterDiscount = subtotal.minus(totalDiscount);

        const taxPct = showTax ? Big(authInfo?.item_tax || 0) : Big(0);
        totalTax = afterDiscount.times(taxPct).div(100);

        state.footerValues.taxPct = Number(taxPct);
      }

      const serviceCharge = Big(state.footerValues.serviceCharge || 0);
      const paidAmount = Big(state.footerValues.paidAmount || 0);
      // const grandTotal = subtotal - totalDiscount + totalTax + serviceCharge;

      const grandTotal =
      state.taxMode === "on_item"
        ? subtotal.plus(serviceCharge)
        : subtotal.minus(totalDiscount).plus(totalTax).plus(serviceCharge);

      state.footerValues = {
        ...state.footerValues, // ✅ keep discountPct, serviceCharge, description, etc.
        subtotal: subtotal.toString(),
        total_discount: totalDiscount.toString(),
        discountPct: resolvedDiscountPct,
        totalTax: totalTax.toString(),
        grandTotal: grandTotal.toString(),
        dueAmount: grandTotal.minus(paidAmount).toString(),
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceExpenseNumber.fulfilled, (state, action) => {
        state.voucherNumber = action.payload;
      })
      .addCase(fetchServiceExpenseAccs.fulfilled, (state, action) => {
        state.serviceExpenseAccs = action.payload;
      })
      .addCase(fetchServiceExpenseRecord.fulfilled, (state, action) => {
        const data = action.payload;
        const details = data?.details || [];
        let taxmodeDetails = (data.is_cal_type == "individual") ? "on_item" : "on_total";
        console.log(taxmodeDetails, data.is_cal_type, "taxmodeDetails");

        console.log(data, "datadata");

        // Header
        state.service_ex_id = data?.service_ex_id;
        state.voucherNumber = data?.service_ex_voucher_no;
        state.entryDate = data?.created_date;
        state.taxMode = taxmodeDetails;

        state.payServiceCart = (data.trans || []).map((t) => ({
          from_acc_id: t.to_acc_id,
          from_acc_name: t.to_acc_name,
          tran_amount: Number(t.tran_amount || 0),
        }));

        // Supplier
        state.supplierInfo = {
          acc_id: data?.acc_id,
          acc_name: data?.acc_name,
          institution_name: data?.institution_name,
          contact_no: data?.contact_no,
          address: data?.address,
        };

        // Footer
        state.footerValues = {
          subtotal: Number(data?.sub_total || 0).toFixed(2),
          totalTax: Number(data?.total_tax || 0).toFixed(2),
          total_discount: Number(data?.total_discount || 0).toFixed(2),
          grandTotal: Number(data?.total_amount || 0).toFixed(2),
          paidAmount: Number(data?.paid_amount || 0),
          dueAmount: Number(data?.due_amount || 0),
          serviceCharge: Number(data?.total_transport_cost || 0),
          description: data?.narration || "",
          discountPct: Number(data?.total_discount_per || 0),
          discountAmt: Number(data?.total_discount || 0),
          discountMode: data?.total_discount_mode || "percent",
          taxPct: Number(data?.total_tax_per || 0),
        };

        // Items
        state.itemCart = details.map((item, index) => ({
          key: Date.now() + index,
          item_id: item.item_id,
          product: item.item_name,
          product_display: item.display_text || item.item_name,
          item_name: item.item_name,
          quantity: Number(item.service_ex_qty || 0),
          rate: Number(item.service_ex_rate || 0),
          discount: Number(item.item_discount_per || 0),
          discountAmt: Number(item.item_discount || 0),
          discountMode: item.discount_mode || "percent",
          tax_per: Number(item.item_tax_per || 0),
          total: Number(item.item_total || 0),
        }));

        state.footerValues.payCart = (data.trans || []).map((t) => ({
          to_acc_id: t.from_acc_id,
          to_acc_name: t.from_acc_name,
          tran_amount: Number(t.tran_amount || 0),
        }));

        state.selectedTaxAcc = {
          acc_id: data.tax_acc_id,
          acc_name: data.tax_acc_name,
        };

        state.selectedDiscountAcc = {
          acc_id: data.discount_acc_id,
          acc_name: data.discount_acc_name,
        };

        state.tag_field_id = data.tag_field_id || [];
        state.conversionRate = Number(data.conversion_rate || 0);
        state.conversionCurrency = data.conversion_currency || null;
        // Payments
        state.payServiceCart = (data?.trans || []).map((t) => ({
          to_acc_id: t.from_acc_id,
          to_acc_name: t.from_acc_name,
          tran_amount: Number(t.tran_amount || 0),
        }));
      })
      // .addCase(fetchServiceExpenseRecord.fulfilled, (state, action) => {
      //   const data = action.payload;
      //   const dataDetails = data?.details || [];
      //   console.log(data, "datadata");

      //   state.footerValues = {
      //     service_ex_id: data?.service_ex_id,
      //     service_ex_voucher_no: data?.service_ex_voucher_no,
      //     order_id: data?.order_id || 0,
      //     is_cal_type: data?.is_cal_type,
      //     narration: data?.narration || "",
      //     sub_total: data?.sub_total,
      //     total_discount_per: data?.total_discount_per,
      //     total_discount: data?.total_discount,
      //     total_tax_per: data?.total_tax_per,
      //     total_tax: data?.total_tax,
      //     total_transport_cost: data?.total_transport_cost,
      //     total_amount: data?.total_amount,
      //     paid_amount: data?.paid_amount ?? 0,
      //     due_amount: data?.due_amount ?? 0,
      //     discount_acc_id: data?.discount_acc_id,
      //     tax_acc_id: data?.tax_acc_id,
      //     transport_acc_id: data?.transport_acc_id,
      //     service_ex_acc_id: data?.service_ex_acc_id,
      //   };

      //   state.itemCart = dataDetails?.map((item) => ({
      //     ...item,
      //     item_code: item.item_code,
      //     serials:
      //       item.is_serial === "yes" && item.serials
      //         ? item.serials.split(",").map((slNo) => ({ serial_number: slNo }))
      //         : [],
      //   }));

      //   state.payCart = data?.trans || [];
      //   state.supplier = {
      //     acc_id: data.acc_id,
      //     acc_name: data.acc_name,
      //     institution_name: data.institution_name,
      //     contact_no: data.contact_no,
      //     address: data.address,
      //   };
      // })
      .addCase(createServiceExpense.pending, (state) => {
        state.loading = true;
      })
      .addCase(createServiceExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload;
      })
      .addCase(createServiceExpense.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  resetServiceExpense,
  setSupplier,
  addOrderRow,
  deleteOrderRow,
  updateOrderRow,
  calculateTotals,
  setPayServiceCart,
  setTagFieldId,
  setDiscountAcc,
  setTaxAcc,
  setConversion,
  setEntryDate,
  setDueDate,
  setFormField,
  setFooterValues,
  setTaxMode,
  setServiceExpenseAcc,
  setTagFeildNameid,
  setConversionRate
} = serviceExpenseSlice.actions;

export default serviceExpenseSlice.reducer;
